import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import {
  GenerateClient,
  GroceriesClient,
  LocationsClient,
  MoviesClient,
  RemindersClient,
  ExercisesClient,
  ExerciseLogsClient,
} from '../Clients/reminds'; // Update the path accordingly
import { useLoginContext } from './Login.Context';

interface ApiClientContextType {
  generateClient: GenerateClient;
  moviesClient: MoviesClient;
  locationsClient: LocationsClient;
  groceriesClient: GroceriesClient;
  remindersClient: RemindersClient;
  exercisesClient: ExercisesClient;
  exerciseLogsClient: ExerciseLogsClient;
}

const ApiClientContext = createContext<ApiClientContextType | undefined>(undefined);

export const useApiClientContext = () => {
  const context = useContext(ApiClientContext);
  if (!context) {
    throw new Error('useApiClientContext must be used within a ApiClientProvider');
  }
  return context;
};

interface ApiClientProviderProps {
  children: ReactNode;
}

const API_URL = import.meta.env.VITE_API_URL;

export const ApiClientProvider: React.FC<ApiClientProviderProps> = ({ children }) => {
  const [generateClient, setGenerateClient] = useState<GenerateClient | null>(null);
  const [moviesClient, setMoviesClient] = useState<MoviesClient | null>(null);
  const [locationsClient, setLocationsClient] = useState<LocationsClient | null>(null);
  const [groceriesClient, setGroceriesClient] = useState<GroceriesClient | null>(null);
  const [remindersClient, setRemindersClient] = useState<RemindersClient | null>(null);
  const [exercisesClient, setExercisesClient] = useState<ExercisesClient | null>(null);
  const [exerciseLogsClient, setExerciseLogsClient] = useState<ExerciseLogsClient | null>(null);

  const { token } = useLoginContext();

  useEffect(() => {
    if (token !== null) {
      const generateClient = new GenerateClient({ tokenKey: 'jwt-token' }, API_URL);
      const moviesClient = new MoviesClient({ tokenKey: 'jwt-token' }, API_URL);
      const locationsClient = new LocationsClient({ tokenKey: 'jwt-token' }, API_URL);
      const groceriesClient = new GroceriesClient({ tokenKey: 'jwt-token' }, API_URL);
      const remindsClient = new RemindersClient({ tokenKey: 'jwt-token' }, API_URL);
      const exercisesClient = new ExercisesClient({ tokenKey: 'jwt-token' }, API_URL);
      const exerciseLogsClient = new ExerciseLogsClient({ tokenKey: 'jwt-token' }, API_URL);
      setGenerateClient(generateClient);
      setLocationsClient(locationsClient);
      setMoviesClient(moviesClient);
      setGroceriesClient(groceriesClient);
      setRemindersClient(remindsClient);
      setExercisesClient(exercisesClient);
      setExerciseLogsClient(exerciseLogsClient);
    } else {
      setGenerateClient(null);
      setLocationsClient(null);
      setMoviesClient(null);
      setGroceriesClient(null);
      setRemindersClient(null);
      setExercisesClient(null);
      setExerciseLogsClient(null);
    }
  }, [token]);

  if (
    !locationsClient ||
    !generateClient ||
    !moviesClient ||
    !groceriesClient ||
    !remindersClient ||
    !exercisesClient ||
    !exerciseLogsClient
  ) {
    return null;
  }
  return (
    <ApiClientContext.Provider
      value={{
        locationsClient,
        generateClient,
        moviesClient,
        groceriesClient,
        remindersClient,
        exercisesClient,
        exerciseLogsClient,
      }}
    >
      {children}
    </ApiClientContext.Provider>
  );
};
